import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FileText, Hash, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { SubscriberListRef } from '@/features/subscribers';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { formatDate, formatDateTime, pluralise } from '@/shared/lib/format';
import type { Draft } from '../api/draftsApi';
import { deliveryProgress } from '../model/draftView';
import { DeliveryList } from './DeliveryList';
import { ListPicker } from './ListPicker';
import { Recipients } from './Recipients';
import { tagSx } from './styles';

export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box component="div" role="row" sx={rowSx}>
      <Typography role="rowheader" variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Box role="cell" sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export function Fields({ children }: { children: ReactNode }) {
  return <Box role="table" sx={tableSx}>{children}</Box>;
}

export function Sources({ draft }: { draft: Draft }) {
  if (draft.sources.length === 0) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No source</Typography>;
  return (
    <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
      {draft.sources.map((s) => (
        <Box key={`${s.documentId}-${s.page}`} component={RouterLink} to={`/documents/${s.documentId}?page=${s.page}`} sx={{ ...tagSx, textDecoration: 'none', '&:hover': { color: 'text.primary' } }}>
          <FileText size={12} aria-hidden />
          {s.title} · {formatDate(s.publishDate)} · p.{s.page}
        </Box>
      ))}
    </Stack>
  );
}

type PendingProps = { draft: Draft; lists: SubscriberListRef[]; listId: string; onList: (id: string) => void; canDecide: boolean };

/** Before approval: the list (changeable by an analyst), who is on it, and the source. */
export function PendingFields({ draft, lists, listId, onList, canDecide }: PendingProps) {
  return (
    <Fields>
      <FieldRow label="Send to">
        {canDecide ? <ListPicker lists={lists} value={listId} onChange={onList} /> : <ListName name={draft.list.name} />}
      </FieldRow>
      <FieldRow label="Recipients"><Recipients list={lists.find((l) => l.id === listId)} /></FieldRow>
      <FieldRow label="Source"><Sources draft={draft} /></FieldRow>
    </Fields>
  );
}

function ListName({ name, count }: { name: string; count?: number }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
      <Hash size={14} aria-hidden />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
      {count === undefined ? null : <Typography variant="body2" sx={{ color: 'text.secondary' }}>· {pluralise(count, 'number')}</Typography>}
    </Stack>
  );
}

function Decider({ name, at }: { name: string; at: string | null }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
      <PersonAvatar name={name} size={20} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
      {at ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>· {formatDateTime(at)}</Typography> : null}
    </Stack>
  );
}

function Delivery({ draft }: { draft: Draft }) {
  const [open, setOpen] = useState(false);
  const p = deliveryProgress(draft.deliveries);
  if (p.total === 0) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>{draft.status === 'sending' ? 'Sending…' : 'No deliveries'}</Typography>;
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 120px', maxWidth: 200, height: 6, borderRadius: 3, bgcolor: 'surface.sunken', overflow: 'hidden' }} aria-hidden>
        <Box sx={{ ...barSx, width: `${(p.done / p.total) * 100}%` }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.done} of {p.total} {p.label}</Typography>
      {p.failed ? <Typography variant="body2" sx={{ color: 'error.main' }}>{p.failed} failed</Typography> : null}
      <Link component="button" type="button" variant="body2" underline="hover" onClick={() => { setOpen(true); }} sx={{ ml: 'auto', fontWeight: 500 }}>Delivery details</Link>
      <Dialog open={open} onClose={() => { setOpen(false); }} fullWidth maxWidth="sm" aria-labelledby="delivery-title">
        <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2 }}>
          <Typography id="delivery-title" variant="h4" component="h2" sx={{ flexGrow: 1 }}>Delivery to {draft.list.name}</Typography>
          <IconButton size="small" aria-label="Close delivery details" onClick={() => { setOpen(false); }}><X size={18} /></IconButton>
        </Stack>
        <Box sx={{ px: 2.5, pb: 2.5 }}><DeliveryList deliveries={draft.deliveries} /></Box>
      </Dialog>
    </Stack>
  );
}

/** After the decision: where it went and who approved it, how far delivery got; or who rejected it and why. */
export function DecidedFields({ draft }: { draft: Draft }) {
  const isRejected = draft.status === 'rejected';
  return (
    <Fields>
      <FieldRow label={isRejected ? 'Was for' : 'Sent to'}><ListName name={draft.list.name} count={draft.deliveries.length || draft.list.count} /></FieldRow>
      {draft.decidedBy ? <FieldRow label={isRejected ? 'Rejected by' : 'Approved by'}><Decider name={draft.decidedBy} at={draft.decidedAt} /></FieldRow> : null}
      {isRejected ? (
        <FieldRow label="Reason"><Typography variant="body2" sx={{ color: 'error.main' }}>{draft.rejectReason}</Typography></FieldRow>
      ) : (
        <FieldRow label="Delivery"><Delivery draft={draft} /></FieldRow>
      )}
      <FieldRow label="Source"><Sources draft={draft} /></FieldRow>
    </Fields>
  );
}

const tableSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  '& > [role=row] + [role=row]': { borderTop: 1, borderColor: 'divider' },
} as const;

const rowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '120px minmax(0, 1fr)' },
  alignItems: 'center',
  gap: { xs: 0.5, sm: 2 },
  px: 2,
  py: 1.25,
  minHeight: 52,
} as const;

const barSx = {
  height: '100%',
  borderRadius: 3,
  bgcolor: 'market.up',
  transformOrigin: 'left',
  '@keyframes deliveryGrow': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
  animation: 'deliveryGrow 700ms cubic-bezier(0.2, 0, 0, 1) both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;
